import { AlertCircle, CheckCircle2, Copy, ExternalLink, KeyRound, ShieldAlert, Sparkles, Wallet } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import Button from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";
import { useRetailerSession } from "@/components/feature/retailer/RetailerSessionContext";
import { mandateService } from "@/services/apiServices";
import type { Mandate } from "@/types/mandate";
import { WALLET_RATES } from "@/pages/retailer/wallet/constants";
import { formatINR } from "../data";

interface GenerateLinkModalProps {
  open: boolean;
  onClose: () => void;
  mandate: Mandate | null;
  onSuccess: (updatedMandate: Mandate) => void;
}

export default function GenerateLinkModal({
  open,
  onClose,
  mandate,
  onSuccess,
}: GenerateLinkModalProps) {
  const { wallet, refreshWallet } = useRetailerSession();
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const creationCharge = wallet?.creationRate ?? WALLET_RATES.CREATION_CHARGE ?? 500;
  const available = wallet?.availableBalance ?? 0;
  const isInsufficient = available < creationCharge;

  useEffect(() => {
    if (open) {
      setDigits(["", "", "", ""]);
      setError("");
      setGeneratedLink(null);
      setCopied(false);
      setSubmitting(false);
      refreshWallet();
      setTimeout(() => {
        inputsRef.current[0]?.focus();
      }, 150);
    }
  }, [open, refreshWallet]);

  function handleClose() {
    if (submitting) return;
    onClose();
  }

  function handleDigitChange(idx: number, val: string) {
    const char = val.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[idx] = char;
    setDigits(next);
    setError("");

    if (char && idx < 3) {
      inputsRef.current[idx + 1]?.focus();
    }
  }

  function handleKeyDown(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  }

  async function handleGenerate() {
    if (!mandate) return;
    const mpin = digits.join("");
    if (mpin.length < 4) {
      setError("Please enter your full 4-digit transaction MPIN");
      return;
    }

    if (isInsufficient) {
      setError(`Insufficient wallet balance. Minimum ₹${creationCharge.toFixed(2)} required.`);
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const res = await mandateService.generateLink(mandate.id || mandate.refNo, {
        mpin,
      });
      const updated: Mandate = res?.data?.mandate ?? {
        ...mandate,
        status: "PENDING_AUTH",
        checkoutUrl: res?.data?.checkoutUrl,
        authLink: res?.data?.authLink,
      };

      refreshWallet();
      setGeneratedLink(res?.data?.checkoutUrl || res?.data?.authLink || `/auth/${mandate.refNo}`);
      onSuccess(updated);
    } catch (err: any) {
      setError(err?.message || "Failed to generate mandate link. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function copyLink() {
    if (!generatedLink) return;
    const fullUrl = generatedLink.startsWith("http")
      ? generatedLink
      : `${window.location.origin}${generatedLink}`;
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  const fullAuthUrl = generatedLink?.startsWith("http")
    ? generatedLink
    : `${window.location.origin}${generatedLink}`;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={generatedLink ? "Mandate Link Ready" : "Generate Mandate Link"}
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
          {generatedLink ? (
            <Button onClick={handleClose}>Done</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                onClick={handleGenerate}
                loading={submitting}
                disabled={digits.join("").length < 4 || isInsufficient}
                leftIcon={<Sparkles className="h-4 w-4" />}
              >
                Generate Link
              </Button>
            </>
          )}
        </div>
      }
    >
      {generatedLink ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-success-bg p-4 text-emerald-900">
            <CheckCircle2 className="h-6 w-6 shrink-0 text-success" />
            <div>
              <p className="text-sm font-semibold">Authorisation Link Generated Successfully</p>
              <p className="text-xs text-emerald-800">
                Mandate is now in <span className="font-semibold">PENDING AUTH</span> state and ready for customer authorisation.
              </p>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
              Customer Authorisation Link
            </label>
            <div className="mt-1.5 flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={fullAuthUrl}
                className="flex-1 rounded-lg border border-line bg-slate-50 px-3 py-2 text-xs font-mono text-ink"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={copyLink}
                leftIcon={<Copy className="h-3.5 w-3.5" />}
              >
                {copied ? "Copied!" : "Copy"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open(fullAuthUrl, "_blank", "noopener,noreferrer")}
                leftIcon={<ExternalLink className="h-3.5 w-3.5" />}
              >
                Preview
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {mandate && (
            <div className="rounded-lg border border-line bg-slate-50/70 p-3.5 text-xs text-ink">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-ink-muted">Customer:</span>{" "}
                  <span className="font-semibold">{mandate.customerName}</span>
                </div>
                <div>
                  <span className="text-ink-muted">Ref No:</span>{" "}
                  <span className="font-mono font-medium">{mandate.refNo}</span>
                </div>
                <div>
                  <span className="text-ink-muted">Monthly EMI:</span>{" "}
                  <span className="font-semibold text-primary-600">{formatINR(mandate.emiAmount)}</span>
                </div>
                <div>
                  <span className="text-ink-muted">Total Payable:</span>{" "}
                  <span className="font-semibold">{formatINR(mandate.totalPayable)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Wallet Charge info */}
          <div className="flex items-start gap-3 rounded-lg border border-sky-200 bg-sky-50/70 p-3 text-xs text-sky-900">
            <Wallet className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Creation Fee: ₹{creationCharge.toFixed(2)}</span>
                <span className="text-sky-700">Available: ₹{available.toFixed(2)}</span>
              </div>
              <p className="mt-0.5 text-[11px] text-sky-800">
                Fee is deducted from your wallet balance upon link generation.
              </p>
            </div>
          </div>

          {isInsufficient && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-danger-bg p-3 text-xs text-red-900">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
              <p>Insufficient balance to generate mandate link. Please top up your wallet first.</p>
            </div>
          )}

          {/* MPIN Entry */}
          <div className="pt-2">
            <label className="flex items-center gap-1.5 text-sm font-semibold text-ink">
              <KeyRound className="h-4 w-4 text-primary-500" />
              Enter 4-digit Transaction MPIN
            </label>
            <p className="mt-0.5 text-xs text-ink-secondary">
              Authorise link generation and deduction of creation fee from your wallet.
            </p>

            <div className="mt-3 flex justify-center gap-3">
              {[0, 1, 2, 3].map((i) => (
                <input
                  key={i}
                  ref={(el) => {
                    inputsRef.current[i] = el;
                  }}
                  type="password"
                  maxLength={1}
                  inputMode="numeric"
                  value={digits[i]}
                  onChange={(e) => handleDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  disabled={submitting || isInsufficient}
                  className="h-12 w-12 text-center text-xl font-bold border rounded-lg border-line focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 bg-white shadow-sm"
                />
              ))}
            </div>

            {error && (
              <div className="mt-3 flex items-center gap-1.5 text-xs text-danger justify-center">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
